import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, DollarSign, Gauge, Minus, Percent, TrendingDown, TrendingUp } from 'lucide-react';
import CompanyPeriodSelector from '../../shared/components/CompanyPeriodSelector';
import EmptyState from '../../shared/components/EmptyState';
import LoadingState from '../../shared/components/LoadingState';
import { generateAiFinancialReport } from '../../services/analysisAiReport';
import { fetchRatioTrend } from '../../services/analysisRatioTrend';
import { fetchSectorRatioAnalysis } from '../../services/analysisSectorRatio';
import { fetchStandardRatios } from '../../services/analysisRatiosStandard';
import { fetchMacroModules } from '../../services/macroData';
import { listSectorRatios } from '../../services/sectorRatios';
import { useLanguage } from '../../app/i18n/LanguageContext';
import { exportMainDashboardPdf } from './dashboardPdfExport';
import './DashboardPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE = baseEnvironment.replace(/\/$/, '');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getConfiguredIndicators = (module) =>
  (module?.indicators || []).filter((item) => item?.snapshot?.source !== 'UNCONFIGURED');

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const pickIndicator = (module, preferredInstruments = []) => {
  const indicators = getConfiguredIndicators(module);
  if (!indicators.length) {
    return null;
  }
  for (const instrument of preferredInstruments) {
    const match = indicators.find((item) => item.instrument === instrument);
    if (match) {
      return match;
    }
  }
  return indicators[0];
};

const formatValue = (value, digits = 2, suffix = '') => {
  const numeric = asNumber(value);
  if (numeric === null) {
    return '-';
  }
  return `${numeric.toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}${suffix}`;
};

const formatSignedPercent = (value) => {
  const numeric = asNumber(value);
  if (numeric === null) {
    return '-';
  }
  const sign = numeric > 0 ? '+' : '';
  return `${sign}${numeric.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`;
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const trendMeta = (changePct, t) => {
  const value = asNumber(changePct);
  if (value === null) {
    return { className: 'trend-neutral', icon: <Minus size={14} />, label: t('trendNone') };
  }
  if (value > 0.2) {
    return { className: 'trend-positive', icon: <TrendingUp size={14} />, label: t('increase') };
  }
  if (value < -0.2) {
    return { className: 'trend-negative', icon: <TrendingDown size={14} />, label: t('decrease') };
  }
  return { className: 'trend-neutral', icon: <Minus size={14} />, label: t('flat') };
};

const ratioFormatValue = (value, unit) => {
  if (value === null || value === undefined) {
    return '-';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  if (unit === '%') {
    return `${(number * 100).toLocaleString('tr-TR', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    })}%`;
  }
  return number.toLocaleString('tr-TR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  });
};

const ratioDeviationPercent = (value, standard) => {
  const v = Number(value);
  const s = Number(standard);
  if (Number.isNaN(v) || Number.isNaN(s) || Math.abs(s) < 0.000001) {
    return null;
  }
  return ((v - s) / Math.abs(s)) * 100;
};

const verdictFromScore = (score, t) => {
  if (score >= 75) {
    return t('strong');
  }
  if (score >= 50) {
    return t('balanced');
  }
  return t('risky');
};

const formatNumber = (value, digits = 2) => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return number.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatPercent = (value, digits = 1) => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${number.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
};

const extractYear = (value) => {
  const matched = String(value || '').match(/\d{4}/);
  return matched ? Number(matched[0]) : null;
};

const getPeriodLabel = (period, locale = 'tr-TR') => period?.code || period?.description || `${locale.startsWith('en') ? 'Period' : 'Donem'} ${period?.id ?? ''}`;

const formatSigned = (value, digits = 1) => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${number > 0 ? '+' : ''}${number.toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`;
};

const riskKeys = new Set(['debt_to_equity_ratio', 'debt_ratio']);

const UI_TEXT = {
  'tr-TR': {
    dashboardTitle: 'CFO Analiz Dashboard',
    dashboardSubtitle: 'Finansal saglik gorunumu ve makro trend takibi',
    exportingPdf: 'PDF olusturuluyor...',
    exportPdf: 'PDF Al',
    macroData: 'Makro Veriler',
    standardRatioAnalysis: 'Standart Rasyo Analizi',
    ratioTrendAnalysis: 'Rasyo Trend Analizi',
    sectorRatioAnalysis: 'Sektorel Rasyo Analizi',
    aiPanelTitle: 'AI Mali Analiz ve Karar Destek Paneli',
    loading: 'Yukleniyor...',
    update: 'Guncelleme',
    calculating: 'Hesaplaniyor...',
    calculate: 'Hesapla',
    trendCalculate: 'Trend Hesapla',
    analyze: 'Analiz Et',
    refreshAnalysis: 'Analizi Yenile',
    createAiReport: 'AI Raporu Olustur',
    analysisLoading: 'Analiz Yukleniyor...',
    creating: 'Olusturuluyor...',
    company: 'Sirket',
    startPeriod: 'Baslangic Donemi',
    endPeriod: 'Bitis Donemi',
    maxPeriod: 'Maksimum Donem',
    selectPeriod: 'Donem seciniz',
    periodN: 'Donem',
    benchmarkSector: 'Benchmark Sektor',
    selectSector: 'Sektor seciniz',
    trendStart: 'Trend Baslangic Donemi',
    trendEnd: 'Trend Bitis Donemi',
    trendMax: 'Trend Maksimum Donem',
    temperature: 'Sicaklik (temperature)',
    score: 'Skor',
    criticalDeviation: 'Kritik Sapma',
    risk: 'Risk',
    action: 'Aksiyon',
    strong: 'Guclu',
    balanced: 'Dengeli',
    risky: 'Riskli',
    flat: 'Yatay',
    trendNone: 'Trend yok',
    increase: 'Artis',
    decrease: 'Azalis',
    noData: 'Veri Yok',
    lowRisk: 'Dusuk Risk',
    caution: 'Dikkat',
    alarm: 'Alarm',
    strengthening: 'Gucleniyor',
    weakening: 'Zayifliyor'
  },
  'en-US': {
    dashboardTitle: 'CFO Analysis Dashboard',
    dashboardSubtitle: 'Financial health overview and macro trend tracking',
    exportingPdf: 'Building PDF...',
    exportPdf: 'Export PDF',
    macroData: 'Macro Data',
    standardRatioAnalysis: 'Standard Ratio Analysis',
    ratioTrendAnalysis: 'Ratio Trend Analysis',
    sectorRatioAnalysis: 'Sector Ratio Analysis',
    aiPanelTitle: 'AI Financial Analysis and Decision Support Panel',
    loading: 'Loading...',
    update: 'Update',
    calculating: 'Calculating...',
    calculate: 'Calculate',
    trendCalculate: 'Calculate Trend',
    analyze: 'Analyze',
    refreshAnalysis: 'Refresh Analysis',
    createAiReport: 'Create AI Report',
    analysisLoading: 'Loading analysis...',
    creating: 'Creating...',
    company: 'Company',
    startPeriod: 'Start Period',
    endPeriod: 'End Period',
    maxPeriod: 'Max Period',
    selectPeriod: 'Select period',
    periodN: 'Period',
    benchmarkSector: 'Benchmark Sector',
    selectSector: 'Select sector',
    trendStart: 'Trend Start Period',
    trendEnd: 'Trend End Period',
    trendMax: 'Trend Max Period',
    temperature: 'Temperature',
    score: 'Score',
    criticalDeviation: 'Critical Deviation',
    risk: 'Risk',
    action: 'Action',
    strong: 'Strong',
    balanced: 'Balanced',
    risky: 'Risky',
    flat: 'Flat',
    trendNone: 'No trend',
    increase: 'Increase',
    decrease: 'Decrease',
    noData: 'No Data',
    lowRisk: 'Low Risk',
    caution: 'Caution',
    alarm: 'Alarm',
    strengthening: 'Strengthening',
    weakening: 'Weakening'
  }
};

const Sparkline = ({ values }) => {
  const valid = (values || []).filter((v) => Number.isFinite(v));
  if (!valid.length) {
    return <div className="sparkline-empty">-</div>;
  }
  const w = 132;
  const h = 34;
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = Math.max(max - min, 0.000001);
  const pts = valid
    .map((value, index) => {
      const x = valid.length === 1 ? w / 2 : (index / (valid.length - 1)) * w;
      const y = h - ((value - min) / range) * h;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="sparkline">
      <polyline points={pts} />
    </svg>
  );
};

function DashboardPage() {
  const { language } = useLanguage();
  const text = UI_TEXT[language] || UI_TEXT['tr-TR'];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);
  const pageCaptureRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [macroModules, setMacroModules] = useState([]);
  const [macroLoading, setMacroLoading] = useState(false);
  const [macroMessage, setMacroMessage] = useState('');

  const [ratioSelection, setRatioSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [ratioData, setRatioData] = useState(null);
  const [ratioLoading, setRatioLoading] = useState(false);
  const [ratioMessage, setRatioMessage] = useState('');

  const [trendCompanies, setTrendCompanies] = useState([]);
  const [trendPeriods, setTrendPeriods] = useState([]);
  const [trendCompanyId, setTrendCompanyId] = useState('');
  const [trendStartPeriodId, setTrendStartPeriodId] = useState('');
  const [trendEndPeriodId, setTrendEndPeriodId] = useState('');
  const [trendMaxPeriods, setTrendMaxPeriods] = useState(12);
  const [trendData, setTrendData] = useState(null);
  const [trendStandards, setTrendStandards] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendMessage, setTrendMessage] = useState('');

  const [sectorSelection, setSectorSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [sectorBenchmarkPeriods, setSectorBenchmarkPeriods] = useState([]);
  const [sectorByPeriodId, setSectorByPeriodId] = useState({});
  const [sectorBenchmarkPeriodId, setSectorBenchmarkPeriodId] = useState('');
  const [sectorOptions, setSectorOptions] = useState([]);
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const [sectorData, setSectorData] = useState(null);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorMessage, setSectorMessage] = useState('');

  const [aiSelection, setAiSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [aiTemperature, setAiTemperature] = useState(0.3);
  const [aiTrendStartPeriodId, setAiTrendStartPeriodId] = useState('');
  const [aiTrendEndPeriodId, setAiTrendEndPeriodId] = useState('');
  const [aiTrendMaxPeriods, setAiTrendMaxPeriods] = useState(4);
  const [aiLoadingAnalysis, setAiLoadingAnalysis] = useState(false);
  const [aiLoadingGenerate, setAiLoadingGenerate] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiStandardData, setAiStandardData] = useState(null);
  const [aiTrendData, setAiTrendData] = useState(null);
  const [aiSectorData, setAiSectorData] = useState(null);
  const [aiDebtScenario, setAiDebtScenario] = useState(null);

  const hasSelection = Boolean(selection.companyId && selection.periodId);
  const hasRatioSelection = Boolean(ratioSelection.companyId && ratioSelection.periodId);
  const hasTrendSelection = Boolean(trendCompanyId && trendStartPeriodId && trendEndPeriodId);
  const hasSectorSelection = Boolean(sectorSelection.companyId && sectorSelection.periodId);
  const hasAiSelection = Boolean(aiSelection.companyId && aiSelection.periodId);

  useEffect(() => {
    if (!hasSelection) {
      setMacroModules([]);
      return;
    }

    let active = true;
    const loadModules = async () => {
      setMacroLoading(true);
      setMacroMessage('');
      try {
        const payload = await fetchMacroModules(selection.companyId, selection.periodId);
        if (active) {
          setMacroModules(Array.isArray(payload) ? payload : []);
        }
      } catch (error) {
        if (active) {
          setMacroModules([]);
          setMacroMessage(error instanceof Error ? error.message : 'Makro veriler yuklenemedi.');
        }
      } finally {
        if (active) {
          setMacroLoading(false);
        }
      }
    };

    loadModules();
    return () => {
      active = false;
    };
  }, [hasSelection, selection.companyId, selection.periodId]);

  useEffect(() => {
    let active = true;

    const loadTrendSelections = async () => {
      try {
        const [companiesResponse, periodsResponse] = await Promise.all([
          fetch(`${API_BASE}/api/companies`, { headers: buildHeaders() }),
          fetch(`${API_BASE}/api/periods`, { headers: buildHeaders() })
        ]);

        if (!companiesResponse.ok || !periodsResponse.ok) {
          throw new Error('Sirket/donem secimleri yuklenemedi.');
        }

        const [companyData, periodData] = await Promise.all([companiesResponse.json(), periodsResponse.json()]);
        if (!active) {
          return;
        }

        const companies = Array.isArray(companyData) ? companyData : [];
        const periods = Array.isArray(periodData) ? periodData : [];
        const orderedPeriods = [...periods].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        setTrendCompanies(companies);
        setTrendPeriods(orderedPeriods);

        if (companies.length) {
          setTrendCompanyId(String(companies[0].id));
        }
        if (orderedPeriods.length) {
          setTrendStartPeriodId(String(orderedPeriods[0].id));
          setTrendEndPeriodId(String(orderedPeriods[orderedPeriods.length - 1].id));
        }
      } catch (error) {
        if (active) {
          setTrendMessage(error instanceof Error ? error.message : 'Secimler yuklenemedi.');
        }
      }
    };

    loadTrendSelections();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadSectorBenchmarks = async () => {
      try {
        const payload = await listSectorRatios({
          isActive: true,
          includeGlobal: true,
          offset: 0,
          limit: 5000
        });
        if (!active) {
          return;
        }

        const periodBuckets = new Map();
        (payload?.items || []).forEach((item) => {
          if (!item?.periodId || !item?.sectorId || !item?.sectorCode || !item?.sectorName) {
            return;
          }
          if (!periodBuckets.has(item.periodId)) {
            periodBuckets.set(item.periodId, {
              id: item.periodId,
              code: item.periodCode,
              description: item.periodDescription,
              sectors: new Map()
            });
          }
          const bucket = periodBuckets.get(item.periodId);
          if (!bucket.sectors.has(item.sectorId)) {
            bucket.sectors.set(item.sectorId, {
              id: item.sectorId,
              code: item.sectorCode,
              name: item.sectorName
            });
          }
        });

        const periodList = Array.from(periodBuckets.values())
          .map((bucket) => ({ id: bucket.id, code: bucket.code, description: bucket.description }))
          .sort((left, right) => String(right.code || '').localeCompare(String(left.code || ''), 'tr-TR'));

        const sectorMap = {};
        periodBuckets.forEach((bucket, periodId) => {
          sectorMap[String(periodId)] = Array.from(bucket.sectors.values()).sort((left, right) =>
            String(left.code).localeCompare(String(right.code), 'tr-TR')
          );
        });

        setSectorBenchmarkPeriods(periodList);
        setSectorByPeriodId(sectorMap);
      } catch (error) {
        if (!active) {
          return;
        }
        setSectorBenchmarkPeriods([]);
        setSectorByPeriodId({});
        setSectorOptions([]);
        setSectorMessage(error instanceof Error ? error.message : 'Sektor benchmark listesi yuklenemedi.');
      }
    };

    loadSectorBenchmarks();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!sectorBenchmarkPeriods.length) {
      setSectorBenchmarkPeriodId('');
      return;
    }

    const currentExists = sectorBenchmarkPeriods.some((period) => String(period.id) === String(sectorBenchmarkPeriodId));
    if (currentExists) {
      return;
    }

    const companyPeriodId = String(sectorSelection.periodId || '');
    const companyMatched = sectorBenchmarkPeriods.find((period) => String(period.id) === companyPeriodId);
    if (companyMatched) {
      setSectorBenchmarkPeriodId(String(companyMatched.id));
      return;
    }

    const companyYear = extractYear(sectorSelection.period?.code);
    if (companyYear) {
      const candidate = sectorBenchmarkPeriods.find((period) => {
        const periodYear = extractYear(period.code);
        return periodYear && periodYear <= companyYear;
      });
      if (candidate) {
        setSectorBenchmarkPeriodId(String(candidate.id));
        return;
      }
    }

    setSectorBenchmarkPeriodId(String(sectorBenchmarkPeriods[0].id));
  }, [sectorBenchmarkPeriods, sectorBenchmarkPeriodId, sectorSelection.periodId, sectorSelection.period]);

  useEffect(() => {
    const currentSectors = sectorByPeriodId[String(sectorBenchmarkPeriodId)] || [];
    setSectorOptions(currentSectors);
  }, [sectorByPeriodId, sectorBenchmarkPeriodId]);

  useEffect(() => {
    if (sectorOptions.length === 0) {
      setSelectedSectorId('');
      return;
    }
    const companySectorRaw = String(
      sectorSelection.company?.sectorCode
      || sectorSelection.company?.sector
      || sectorSelection.company?.sectorName
      || ''
    )
      .trim()
      .toUpperCase();
    const matched = sectorOptions.find((sector) => String(sector.code).trim().toUpperCase() === companySectorRaw)
      || sectorOptions.find((sector) => String(sector.name).trim().toUpperCase() === companySectorRaw)
      || sectorOptions[0];
    if (matched) {
      setSelectedSectorId(String(matched.id));
    }
  }, [sectorSelection.company, sectorOptions]);

  const orderedTrendPeriods = useMemo(
    () => [...trendPeriods].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
    [trendPeriods]
  );

  const selectedStartDate = useMemo(
    () => orderedTrendPeriods.find((period) => String(period.id) === trendStartPeriodId)?.startDate,
    [orderedTrendPeriods, trendStartPeriodId]
  );

  const selectedEndDate = useMemo(
    () => orderedTrendPeriods.find((period) => String(period.id) === trendEndPeriodId)?.startDate,
    [orderedTrendPeriods, trendEndPeriodId]
  );

  const filteredStartPeriods = useMemo(() => {
    if (!selectedEndDate) {
      return orderedTrendPeriods;
    }
    return orderedTrendPeriods.filter((period) => new Date(period.startDate) <= new Date(selectedEndDate));
  }, [orderedTrendPeriods, selectedEndDate]);

  const filteredEndPeriods = useMemo(() => {
    if (!selectedStartDate) {
      return orderedTrendPeriods;
    }
    return orderedTrendPeriods.filter((period) => new Date(period.startDate) >= new Date(selectedStartDate));
  }, [orderedTrendPeriods, selectedStartDate]);

  const selectedAiTrendStartDate = useMemo(
    () => orderedTrendPeriods.find((period) => String(period.id) === aiTrendStartPeriodId)?.startDate,
    [orderedTrendPeriods, aiTrendStartPeriodId]
  );

  const selectedAiTrendEndDate = useMemo(
    () => orderedTrendPeriods.find((period) => String(period.id) === aiTrendEndPeriodId)?.startDate,
    [orderedTrendPeriods, aiTrendEndPeriodId]
  );

  const filteredAiTrendStartPeriods = useMemo(() => {
    if (!selectedAiTrendEndDate) {
      return orderedTrendPeriods;
    }
    const endTime = new Date(selectedAiTrendEndDate).getTime();
    if (Number.isNaN(endTime)) {
      return orderedTrendPeriods;
    }
    return orderedTrendPeriods.filter((period) => {
      const periodTime = new Date(period.startDate || '').getTime();
      return Number.isNaN(periodTime) ? true : periodTime <= endTime;
    });
  }, [orderedTrendPeriods, selectedAiTrendEndDate]);

  const filteredAiTrendEndPeriods = useMemo(() => {
    if (!selectedAiTrendStartDate) {
      return orderedTrendPeriods;
    }
    const startTime = new Date(selectedAiTrendStartDate).getTime();
    if (Number.isNaN(startTime)) {
      return orderedTrendPeriods;
    }
    return orderedTrendPeriods.filter((period) => {
      const periodTime = new Date(period.startDate || '').getTime();
      return Number.isNaN(periodTime) ? true : periodTime >= startTime;
    });
  }, [orderedTrendPeriods, selectedAiTrendStartDate]);

  useEffect(() => {
    if (!orderedTrendPeriods.length) {
      setAiTrendStartPeriodId('');
      setAiTrendEndPeriodId('');
      return;
    }

    const endIndexFromSelection = orderedTrendPeriods.findIndex((period) => String(period.id) === String(aiSelection.periodId));
    const endIndex = endIndexFromSelection >= 0 ? endIndexFromSelection : orderedTrendPeriods.length - 1;
    const startIndex = Math.max(0, endIndex - 3);

    setAiTrendStartPeriodId(String(orderedTrendPeriods[startIndex].id));
    setAiTrendEndPeriodId(String(orderedTrendPeriods[endIndex].id));
    setAiTrendMaxPeriods(endIndex - startIndex + 1);
  }, [orderedTrendPeriods, aiSelection.periodId]);

  const handleRatioCalculate = async () => {
    if (!hasRatioSelection) {
      setRatioMessage('Lutfen sirket ve donem secin.');
      return;
    }
    setRatioLoading(true);
    setRatioMessage('');
    try {
      const payload = await fetchStandardRatios(ratioSelection.companyId, ratioSelection.periodId);
      setRatioData(payload);
    } catch (error) {
      setRatioData(null);
      setRatioMessage(error instanceof Error ? error.message : 'Rasyo analizi yuklenemedi.');
    } finally {
      setRatioLoading(false);
    }
  };

  const handleTrendCalculate = async () => {
    if (!hasTrendSelection) {
      setTrendMessage('Lutfen sirket ve donem araligi secin.');
      return;
    }

    setTrendLoading(true);
    setTrendMessage('');
    try {
      const trendResult = await fetchRatioTrend(trendCompanyId, trendStartPeriodId, trendEndPeriodId, trendMaxPeriods);
      const activePeriodId = trendResult?.items?.length
        ? String(trendResult.items[trendResult.items.length - 1].periodId)
        : trendEndPeriodId;

      const standardResult = await fetchStandardRatios(trendCompanyId, activePeriodId);
      setTrendData(trendResult);
      setTrendStandards(standardResult?.items || []);
    } catch (error) {
      setTrendData(null);
      setTrendStandards([]);
      setTrendMessage(error instanceof Error ? error.message : 'Trend hesaplanamadi.');
    } finally {
      setTrendLoading(false);
    }
  };

  const handleSectorAnalyze = async () => {
    if (!hasSectorSelection) {
      setSectorMessage('Lutfen sirket ve donem secin.');
      return;
    }
    if (!sectorBenchmarkPeriodId) {
      setSectorMessage('Lutfen sektor rasyo donemi secin.');
      return;
    }
    if (!selectedSectorId) {
      setSectorMessage('Lutfen benchmark olan bir sektor secin.');
      return;
    }

    setSectorLoading(true);
    setSectorMessage('');
    try {
      const payload = await fetchSectorRatioAnalysis(
        sectorSelection.companyId,
        sectorSelection.periodId,
        selectedSectorId || undefined,
        sectorBenchmarkPeriodId || undefined
      );
      setSectorData(payload);
    } catch (error) {
      setSectorData(null);
      setSectorMessage(error instanceof Error ? error.message : 'Sektorel rasyo analizi hesaplanamadi.');
    } finally {
      setSectorLoading(false);
    }
  };

  const loadAiAnalysis = async (companyId, periodId) => {
    if (!companyId || !periodId || !aiTrendStartPeriodId || !aiTrendEndPeriodId) {
      return;
    }
    setAiLoadingAnalysis(true);
    setAiMessage('');
    try {
      const [std, trn, sec] = await Promise.all([
        fetchStandardRatios(companyId, periodId),
        fetchRatioTrend(companyId, aiTrendStartPeriodId, aiTrendEndPeriodId, aiTrendMaxPeriods),
        fetchSectorRatioAnalysis(companyId, periodId)
      ]);
      setAiStandardData(std);
      setAiTrendData(trn);
      setAiSectorData(sec);
      const debt = (std?.items || []).find((item) => item.key === 'debt_to_equity_ratio');
      setAiDebtScenario(Number(debt?.calculatedValue || 0));
    } catch (error) {
      setAiMessage(error instanceof Error ? error.message : 'Analiz verisi yuklenemedi.');
      setAiStandardData(null);
      setAiTrendData(null);
      setAiSectorData(null);
    } finally {
      setAiLoadingAnalysis(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!hasAiSelection) {
      setAiMessage('Lutfen sirket ve donem secin.');
      return;
    }
    setAiLoadingGenerate(true);
    setAiMessage('');
    try {
      await generateAiFinancialReport(aiSelection.companyId, aiSelection.periodId, 'IFRS', language, aiTemperature);
      setAiMessage('AI raporu olusturuldu.');
      await loadAiAnalysis(aiSelection.companyId, aiSelection.periodId);
    } catch (error) {
      setAiMessage(error instanceof Error ? error.message : 'AI raporu olusturulamadi.');
    } finally {
      setAiLoadingGenerate(false);
    }
  };

  const macroCards = useMemo(() => {
    const moduleMap = new Map((macroModules || []).map((module) => [module.key, module]));

    const tufe = pickIndicator(moduleMap.get('price-indices'), ['CPI_YOY_TR', 'PPI_YOY_TR']);
    const interest = pickIndicator(moduleMap.get('interest-profit-share'), [
      'COMMERCIAL_LOAN_TL_FLOW_RATE_TR',
      'CONSUMER_LOAN_TL_FLOW_RATE_TR'
    ]);
    const fx = pickIndicator(moduleMap.get('fx'), ['USDTRY', 'EURTRY']);
    const capacity = pickIndicator(moduleMap.get('capacity-utilization'), ['CAPACITY_UTILIZATION_TR', 'CAPACITY_UTILIZATION_TR_SA']);

    return [
      {
        key: 'tufe',
        title: language.startsWith('en') ? 'CPI' : 'TUFE',
        icon: <Activity size={18} />,
        value: formatValue(tufe?.snapshot?.value, 1, '%'),
        changePct: tufe?.snapshot?.changePct,
        updatedAt: formatDateTime(tufe?.snapshot?.asOf || tufe?.snapshot?.sourceTimestamp)
      },
      {
        key: 'interest',
        title: language.startsWith('en') ? 'Interest' : 'Faiz',
        icon: <Percent size={18} />,
        value: formatValue(interest?.snapshot?.value, 2, '%'),
        changePct: interest?.snapshot?.changePct,
        updatedAt: formatDateTime(interest?.snapshot?.asOf || interest?.snapshot?.sourceTimestamp)
      },
      {
        key: 'fx',
        title: language.startsWith('en') ? 'FX (USD/TRY)' : 'Doviz (USD/TRY)',
        icon: <DollarSign size={18} />,
        value: formatValue(fx?.snapshot?.value, 2),
        changePct: fx?.snapshot?.changePct,
        updatedAt: formatDateTime(fx?.snapshot?.asOf || fx?.snapshot?.sourceTimestamp)
      },
      {
        key: 'capacity',
        title: language.startsWith('en') ? 'Capacity Utilization' : 'Kapasite Kullanim',
        icon: <Gauge size={18} />,
        value: formatValue(capacity?.snapshot?.value, 1, '%'),
        changePct: capacity?.snapshot?.changePct,
        updatedAt: formatDateTime(capacity?.snapshot?.asOf || capacity?.snapshot?.sourceTimestamp)
      }
    ];
  }, [macroModules, language]);
  const hasMacroData = useMemo(
    () => macroCards.some((card) => card.value !== '-' || card.updatedAt !== '-'),
    [macroCards]
  );

  const ratioRows = useMemo(() => ratioData?.items || [], [ratioData]);
  const ratioSummary = useMemo(
    () => ({ aboveCount: ratioData?.aboveCount || 0, noDataCount: ratioData?.noDataCount || 0 }),
    [ratioData]
  );

  const preparedRatioRows = useMemo(
    () => ratioRows.map((row) => {
      const deviation = ratioDeviationPercent(row.calculatedValue, row.standardValue) || 0;
      const comparison = String(row.comparison || '').toLowerCase();
      const issueType = comparison === 'no_data'
        ? 'data_quality'
        : comparison === 'below'
          ? (row.key === 'debt_to_equity_ratio' || row.key === 'debt_ratio' ? 'risk' : 'performance')
          : comparison === 'equal'
            ? 'neutral'
            : 'positive';
      return { ...row, deviation, issueType };
    }),
    [ratioRows]
  );

  const ratioScore = useMemo(() => {
    const positives = preparedRatioRows.filter((row) => row.issueType === 'positive').length;
    const critical = preparedRatioRows.filter((row) => (row.issueType === 'risk' || row.issueType === 'performance') && Math.abs(row.deviation) >= 25).length;
    const missing = preparedRatioRows.filter((row) => row.issueType === 'data_quality').length;
    return clamp((positives * 7) - (critical * 12) - (missing * 6) + 42, 0, 100);
  }, [preparedRatioRows]);

  const ratioVerdict = verdictFromScore(ratioScore, t);
  const ratioCoverage = preparedRatioRows.length
    ? clamp(((preparedRatioRows.length - ratioSummary.noDataCount) / preparedRatioRows.length) * 100, 0, 100)
    : 0;

  const leverageRow = preparedRatioRows.find((row) => row.key === 'debt_to_equity_ratio');
  const leverageRisk = !leverageRow
    ? t('noData')
    : Number(leverageRow.calculatedValue) <= Number(leverageRow.standardValue)
      ? t('lowRisk')
      : Number(leverageRow.calculatedValue) <= Number(leverageRow.standardValue) * 1.35
        ? t('caution')
        : t('alarm');

  const criticalRow = [...preparedRatioRows].sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation))[0];
  const missingCount = preparedRatioRows.filter((row) => row.issueType === 'data_quality').length;
  const suspiciousCount = preparedRatioRows.filter((row) => Math.abs(row.deviation) > 300).length;
  const trustScore = clamp(100 - (missingCount * 10) - (suspiciousCount * 6), 0, 100);

  const trendRatioOrder = useMemo(() => trendStandards.map((item) => item.key), [trendStandards]);

  const trendSummary = useMemo(() => {
    if (!trendData?.items?.length || trendData.items.length < 2) {
      return { up: 0, down: 0, flat: 0 };
    }

    const first = trendData.items[0].metrics || {};
    const last = trendData.items[trendData.items.length - 1].metrics || {};

    let up = 0;
    let down = 0;
    let flat = 0;

    trendRatioOrder.forEach((key) => {
      const start = first[key];
      const end = last[key];
      if (start === null || start === undefined || end === null || end === undefined) {
        return;
      }
      const delta = Number(end) - Number(start);
      if (Math.abs(delta) < 0.0001) {
        flat += 1;
      } else if (delta > 0) {
        up += 1;
      } else {
        down += 1;
      }
    });

    return { up, down, flat };
  }, [trendData, trendRatioOrder]);

  const trendPointsByMetric = useMemo(() => {
    if (!trendData?.items?.length) {
      return [];
    }

    const primaryMetric = trendStandards[0];
    if (!primaryMetric?.key) {
      return [];
    }

    const standard = Number(primaryMetric.standardValue || 0);
    return trendData.items
      .map((item) => {
        const value = Number(item.metrics?.[primaryMetric.key]);
        if (Number.isNaN(value)) {
          return null;
        }
        return {
          value,
          violation: primaryMetric.higherIsBetter === false ? value > standard : value < standard
        };
      })
      .filter(Boolean);
  }, [trendData, trendStandards]);

  const trendDirection = useMemo(() => {
    if (!trendPointsByMetric.length || trendPointsByMetric.length < 2) {
      return t('flat');
    }
    const start = trendPointsByMetric[0].value;
    const end = trendPointsByMetric[trendPointsByMetric.length - 1].value;
    if (Math.abs(end - start) < 0.0001) {
      return t('flat');
    }
    return end > start ? t('strengthening') : t('weakening');
  }, [trendPointsByMetric, t]);

  const volatility = useMemo(() => {
    if (trendPointsByMetric.length < 2) {
      return 0;
    }
    const deltas = [];
    for (let index = 1; index < trendPointsByMetric.length; index += 1) {
      deltas.push(Math.abs(trendPointsByMetric[index].value - trendPointsByMetric[index - 1].value));
    }
    return deltas.reduce((acc, item) => acc + item, 0) / deltas.length;
  }, [trendPointsByMetric]);

  const violationsCount = trendPointsByMetric.filter((point) => point.violation).length;
  const trendCoverage = trendPointsByMetric.length
    ? clamp(((trendPointsByMetric.length - violationsCount) / trendPointsByMetric.length) * 100, 0, 100)
    : 0;
  const trendScore = clamp((trendSummary.up * 8) - (trendSummary.down * 7) - (violationsCount * 6) + 55, 0, 100);

  const sectorRows = useMemo(() => sectorData?.items || [], [sectorData]);
  const sectorSummary = useMemo(
    () => ({
      aboveCount: sectorData?.aboveCount || 0,
      belowCount: sectorData?.belowCount || 0,
      noBenchmarkCount: sectorData?.noBenchmarkCount || 0
    }),
    [sectorData]
  );

  const benchmarkScore = useMemo(() => {
    const score = (sectorSummary.aboveCount * 8) - (sectorSummary.belowCount * 10) - (sectorSummary.noBenchmarkCount * 6) + 50;
    return clamp(score, 0, 100);
  }, [sectorSummary]);

  const benchmarkVerdict = verdictFromScore(benchmarkScore, t);

  const preparedSectorRows = useMemo(
    () =>
      sectorRows.map((row) => {
        const company = Number(row.calculatedValue || 0);
        const sector = Number(row.sectorValue || 0);
        const deviation = ratioDeviationPercent(company, sector) || 0;
        const comparison = String(row.comparison || '').toLowerCase();
        const issueType = comparison === 'no_benchmark' ? 'data_quality' : comparison === 'below' ? 'risk' : comparison === 'equal' ? 'neutral' : 'positive';
        return {
          ...row,
          company,
          sector,
          deviation,
          issueType
        };
      }),
    [sectorRows]
  );

  const sectorLeverageRow = preparedSectorRows.find((row) => row.key === 'debt_to_equity_ratio');
  const leverageValue = Number(sectorLeverageRow?.calculatedValue);
  const leverageSector = Number(sectorLeverageRow?.sectorValue || 1);
  const leverageRiskLevel = Number.isNaN(leverageValue)
    ? t('noData')
    : leverageValue <= leverageSector
      ? t('lowRisk')
      : leverageValue <= leverageSector * 1.35
        ? t('caution')
        : t('alarm');

  const sectorCriticalRows = useMemo(
    () => [...preparedSectorRows].sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation)).slice(0, 5),
    [preparedSectorRows]
  );

  const sectorAiSnapshot = {
    score: benchmarkScore,
    verdict: benchmarkVerdict,
    critical: sectorCriticalRows[0]?.title || '-'
  };

  const aiIssueRows = useMemo(() => {
    return (aiStandardData?.items || []).map((item) => {
      const value = Number(item.calculatedValue);
      const standard = Number(item.standardValue);
      const missing = item.calculatedValue === null || item.calculatedValue === undefined;
      if (missing) {
        return { ...item, issueType: 'data_quality' };
      }
      const good = item.higherIsBetter === false ? value <= standard : value >= standard;
      if (good) {
        return { ...item, issueType: 'positive' };
      }
      return { ...item, issueType: riskKeys.has(item.key) ? 'risk' : 'performance' };
    });
  }, [aiStandardData]);

  const aiHealthScore = useMemo(() => {
    const positive = aiIssueRows.filter((row) => row.issueType === 'positive').length;
    const critical = aiIssueRows.filter((row) => {
      if (row.issueType !== 'risk' && row.issueType !== 'performance') {
        return false;
      }
      const dev = Math.abs(ratioDeviationPercent(row.calculatedValue, row.standardValue) || 0);
      return dev >= 25;
    }).length;
    const missing = aiIssueRows.filter((row) => row.issueType === 'data_quality').length;
    return clamp(42 + (positive * 7) - (critical * 12) - (missing * 6), 0, 100);
  }, [aiIssueRows]);

  const aiTrendHealth = useMemo(() => {
    if (!aiTrendData?.items?.length) {
      return [];
    }
    return aiTrendData.items.map((row) => {
      const score = (aiStandardData?.items || []).reduce((acc, item) => {
        const value = Number(row.metrics?.[item.key]);
        const standard = Number(item.standardValue);
        if (Number.isNaN(value) || Number.isNaN(standard)) {
          return acc;
        }
        const good = item.higherIsBetter === false ? value <= standard : value >= standard;
        return acc + (good ? 7 : -3);
      }, 42);
      return { periodCode: row.periodCode, score: clamp(score, 0, 100) };
    });
  }, [aiTrendData, aiStandardData]);

  const aiPrevScore = aiTrendHealth.length > 1 ? aiTrendHealth[aiTrendHealth.length - 2].score : aiHealthScore;
  const aiScoreDelta = aiHealthScore - aiPrevScore;
  const aiVerdict = aiHealthScore >= 70 ? t('strong') : aiHealthScore >= 45 ? t('balanced') : t('risky');
  const aiScoreBand = aiHealthScore >= 70 ? 'ok' : aiHealthScore >= 45 ? 'warn' : 'risk';
  const aiWeakRows = aiIssueRows.filter((row) => row.issueType === 'risk' || row.issueType === 'performance');
  const aiPositiveRows = aiIssueRows.filter((row) => row.issueType === 'positive');

  const aiTopDeviationRows = useMemo(() => {
    const all = aiIssueRows
      .filter((row) => row.calculatedValue !== null && row.standardValue !== null)
      .map((row) => {
        const pct = ratioDeviationPercent(row.calculatedValue, row.standardValue) || 0;
        return { ...row, pct, capped: clamp(pct, -250, 250) };
      });
    const bad = all.filter((row) => row.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, 2);
    const good = all.filter((row) => row.pct >= 0).sort((a, b) => b.pct - a.pct).slice(0, 3);
    return [...bad, ...good];
  }, [aiIssueRows]);

  const aiSectorRows = useMemo(() => {
    const stdMap = new Map((aiStandardData?.items || []).map((item) => [item.key, Number(item.standardValue || 0)]));
    return (aiSectorData?.items || []).slice(0, 8).map((item) => {
      const company = Number(item.calculatedValue || 0);
      const sector = Number(item.sectorValue || 0);
      const standard = stdMap.get(item.key) || 0;
      const max = Math.max(company, sector, standard, 0.0001) * 1.2;
      return { ...item, company, sector, standard, max };
    });
  }, [aiSectorData, aiStandardData]);

  const aiDebtItem = (aiStandardData?.items || []).find((item) => item.key === 'debt_to_equity_ratio');
  const aiDebtCurrent = Number(aiDebtItem?.calculatedValue);
  const aiDebtStandard = Number(aiDebtItem?.standardValue || 1.5);
  const aiDebtValid = !Number.isNaN(aiDebtCurrent);
  const aiRiskLevel = !aiDebtValid ? 'unknown' : aiDebtCurrent <= aiDebtStandard ? 'low' : aiDebtCurrent <= aiDebtStandard * 1.35 ? 'medium' : 'high';
  const aiSimLevel = aiDebtScenario <= aiDebtStandard ? 'low' : aiDebtScenario <= aiDebtStandard * 1.35 ? 'medium' : 'high';
  const aiSimGain = aiDebtValid ? Math.max(0, (aiDebtCurrent - aiDebtScenario) * 6) : 0;

  const aiSummaryCards = useMemo(() => {
    const best = [...aiPositiveRows].sort((a, b) => Math.abs(ratioDeviationPercent(b.calculatedValue, b.standardValue) || 0) - Math.abs(ratioDeviationPercent(a.calculatedValue, a.standardValue) || 0))[0];
    const worst = [...aiWeakRows].sort((a, b) => (ratioDeviationPercent(a.calculatedValue, a.standardValue) || 0) - (ratioDeviationPercent(b.calculatedValue, b.standardValue) || 0))[0];
    return [
      best ? `${t('strong')}: ${best.title} (${formatSigned(ratioDeviationPercent(best.calculatedValue, best.standardValue), 1)}%).` : '-',
      worst ? `${t('risk')}: ${worst.title} (${formatSigned(ratioDeviationPercent(worst.calculatedValue, worst.standardValue), 1)}%).` : '-',
      worst ? `${t('action')}: ${riskKeys.has(worst.key) ? 'debt reduction plan' : 'operational efficiency plan'}.` : '-'
    ];
  }, [aiPositiveRows, aiWeakRows, t]);

  const aiKpiCards = [
    {
      key: 'score',
      label: language.startsWith('en') ? 'Financial Health' : 'Finansal Saglik',
      value: formatNumber(aiHealthScore, 0),
      delta: formatSigned(aiScoreDelta, 1),
      note: `${language.startsWith('en') ? 'AI Verdict' : 'AI Verdict'}: ${aiVerdict}`,
      status: aiScoreBand,
      spark: aiTrendHealth.map((row) => row.score)
    },
    {
      key: 'above',
      label: language.startsWith('en') ? 'Above Standard Ratio' : 'Standart Ustu Rasyo',
      value: String(aiStandardData?.aboveCount || 0),
      delta: formatSigned((aiStandardData?.aboveCount || 0) - (aiStandardData?.belowCount || 0), 0),
      note: formatPercent(((aiStandardData?.aboveCount || 0) / Math.max(1, (aiStandardData?.items || []).length)) * 100, 0),
      status: (aiStandardData?.aboveCount || 0) >= (aiStandardData?.belowCount || 0) ? 'ok' : 'warn',
      spark: aiTrendHealth.map((row) => row.score)
    },
    {
      key: 'alarm',
      label: language.startsWith('en') ? 'Alarming Ratio' : 'Alarm Veren Rasyo',
      value: String(aiWeakRows.length),
      delta: formatSigned(-aiWeakRows.length, 0),
      note: `${aiRiskLevel.toUpperCase()} leverage`,
      status: aiWeakRows.length > 3 ? 'risk' : aiWeakRows.length > 0 ? 'warn' : 'ok',
      spark: aiTopDeviationRows.map((row) => 100 - Math.abs(row.capped))
    },
    {
      key: 'sector',
      label: language.startsWith('en') ? 'Above Sector' : 'Sektor Uzeri',
      value: String(aiSectorData?.aboveCount || 0),
      delta: formatSigned((aiSectorData?.aboveCount || 0) - (aiSectorData?.belowCount || 0), 0),
      note: `${aiSectorData?.noBenchmarkCount || 0} ${language.startsWith('en') ? 'no benchmark' : 'benchmark yok'}`,
      status: (aiSectorData?.aboveCount || 0) >= (aiSectorData?.belowCount || 0) ? 'ok' : 'risk',
      spark: aiSectorRows.map((row) => (row.company / row.max) * 100)
    }
  ];

  const selectedPeriodLabel = selection.period?.code || selection.period?.description || '-';
  const selectedCompanyLabel = selection.company?.name || '-';

  const handleExportPdf = useCallback(async () => {
    if (!pageCaptureRef.current || exportingPdf) {
      return;
    }
    try {
      setExportingPdf(true);
      await exportMainDashboardPdf({
        element: pageCaptureRef.current,
        companyName: selectedCompanyLabel,
        periodLabel: selectedPeriodLabel,
        language
      });
    } catch (error) {
      setMacroMessage(error instanceof Error ? error.message : 'PDF olusturulamadi.');
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, language, selectedCompanyLabel, selectedPeriodLabel]);

  return (
    <div className="dashboard-page" ref={pageCaptureRef}>
      <header className="dashboard-title-area">
        <div>
          <h1>{t('dashboardTitle')}</h1>
          <p>{t('dashboardSubtitle')}</p>
        </div>
        <div className="dashboard-title-actions" data-export-hidden="true">
          <button type="button" className="btn-secondary" onClick={handleExportPdf} disabled={exportingPdf}>
            {exportingPdf ? t('exportingPdf') : t('exportPdf')}
          </button>
        </div>
      </header>

      <section className="dashboard-section macro-section">
        <div className="section-header">
          <h2>{t('macroData')}</h2>
          <span>{`${selectedCompanyLabel} | ${selectedPeriodLabel}`}</span>
        </div>

        <CompanyPeriodSelector onSelectionChange={setSelection} locale={language} />
        {macroMessage && <p className="dashboard-message">{macroMessage}</p>}

        {macroLoading ? <LoadingState message={t('loading')} /> : null}
        {!macroLoading && !hasSelection ? <EmptyState title={t('noData')} description={language.startsWith('en') ? 'Select company and period to load macro cards.' : 'Makro kartlar icin sirket ve donem secin.'} /> : null}
        {!macroLoading && hasSelection && !hasMacroData ? <EmptyState title={t('noData')} description={language.startsWith('en') ? 'No configured macro indicator data was found.' : 'Konfigure makro gosterge verisi bulunamadi.'} /> : null}
        {!macroLoading && hasSelection && hasMacroData ? (
          <div className="macro-grid">
            {macroCards.map((card) => {
              const trend = trendMeta(card.changePct, t);
              return (
                <article key={card.key} className="macro-card">
                  <div className="macro-card-top">
                    <div className="macro-icon">{card.icon}</div>
                    <h3>{card.title}</h3>
                  </div>
                  <p className="macro-value">{card.value}</p>
                  <div className={`macro-trend ${trend.className}`}>
                    <span className="macro-trend-icon">{trend.icon}</span>
                    <strong>{formatSignedPercent(card.changePct)}</strong>
                    <small>{trend.label}</small>
                  </div>
                  <p className="macro-updated">{`${t('update')}: ${card.updatedAt}`}</p>
                </article>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="dashboard-section">
        <div className="section-header">
          <h2>{t('standardRatioAnalysis')}</h2>
        </div>

        <div className="sr-top-head">
          <div>
            <h3>Standard Intelligence</h3>
            <p>{language.startsWith('en') ? 'Compliance, deviation and risk view for CEOs and CFOs.' : 'CEO ve CFO icin sirketin standartlara uyum, sapma ve risk gorunumu.'}</p>
          </div>
          <button type="button" className="btn-primary sr-calc-btn" onClick={handleRatioCalculate} disabled={!hasRatioSelection || ratioLoading}>
            {ratioLoading ? t('calculating') : t('calculate')}
          </button>
        </div>

        <CompanyPeriodSelector onSelectionChange={setRatioSelection} locale={language} />
        {ratioMessage && <p className="dashboard-message">{ratioMessage}</p>}

        <section className="sr-executive-grid">
          <article className="sr-exec-card sr-gauge-card">
            <header><span>{language.startsWith('en') ? 'Overall Compliance Score' : 'Genel Uyum Skoru'}</span><strong>{ratioFormatValue(ratioScore, null)}</strong></header>
            <div className="sr-gauge" aria-hidden="true"><div className="sr-gauge-center">{ratioFormatValue(ratioScore, null)}</div></div>
            <footer><span className={`sr-verdict sr-verdict-${ratioVerdict.toLowerCase()}`}>{ratioVerdict}</span></footer>
          </article>

          <article className="sr-exec-card">
            <header><span>{language.startsWith('en') ? 'Above Standard Density' : 'Standart Ustu Yogunluk'}</span><strong>{ratioSummary.aboveCount}/{preparedRatioRows.length || 0}</strong></header>
            <div className="sr-progress"><span style={{ width: `${preparedRatioRows.length ? (ratioSummary.aboveCount / preparedRatioRows.length) * 100 : 0}%` }} /></div>
            <footer><span>{`${ratioFormatValue(ratioCoverage, null)}% coverage`}</span></footer>
          </article>

          <article className="sr-exec-card">
            <header><span>{t('leverageAlarm')}</span><strong>{leverageRisk}</strong></header>
            <div className="sr-thermometer"><span style={{ height: `${!leverageRow || Number(leverageRow.standardValue) <= 0 ? 0 : clamp((Number(leverageRow.calculatedValue) / (Number(leverageRow.standardValue) * 2)) * 100, 0, 100)}%` }} /></div>
            <footer><span>{`${t('debtEquity')}: ${ratioFormatValue(leverageRow?.calculatedValue, leverageRow?.unit)} | Std ${ratioFormatValue(leverageRow?.standardValue, leverageRow?.unit)}`}</span></footer>
          </article>

          <article className="sr-exec-card">
            <header><span>AI Snapshot</span><strong>{ratioVerdict}</strong></header>
            <div className="sr-mini-snapshot">
              <div><small>{t('score')}</small><b>{ratioFormatValue(ratioScore, null)}</b></div>
              <div><small>{t('criticalDeviation')}</small><b>{criticalRow?.title || '-'}</b></div>
              <div><small>Data Trust</small><b>{`${ratioFormatValue(trustScore, null)}%`}</b></div>
            </div>
          </article>
        </section>
      </section>

      <section className="dashboard-section">
        <div className="section-header">
          <h2>{t('ratioTrendAnalysis')}</h2>
        </div>

        <div className="rt-top-head">
          <div>
            <h3>Trend Intelligence</h3>
            <p>{language.startsWith('en') ? 'Summary of ratio momentum, threshold violations and critical moves for CEOs and CFOs.' : 'CEO ve CFO icin donemsel rasyo momentumu, esik ihlalleri ve kritik hareketlerin ozeti.'}</p>
          </div>
          <button type="button" className="btn-primary rt-calc-btn" onClick={handleTrendCalculate} disabled={trendLoading || !hasTrendSelection}>
            {trendLoading ? t('calculating') : t('trendCalculate')}
          </button>
        </div>

        <section className="ratio-trend-controls">
          <label>
            {t('company')}
            <select value={trendCompanyId} onChange={(event) => setTrendCompanyId(event.target.value)}>
              {trendCompanies.map((company) => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </label>
          <label>
            {t('startPeriod')}
            <select value={trendStartPeriodId} onChange={(event) => setTrendStartPeriodId(event.target.value)}>
              {filteredStartPeriods.map((period) => (
                <option key={period.id} value={period.id}>{period.code || period.description || period.id}</option>
              ))}
            </select>
          </label>
          <label>
            {t('endPeriod')}
            <select value={trendEndPeriodId} onChange={(event) => setTrendEndPeriodId(event.target.value)}>
              {filteredEndPeriods.map((period) => (
                <option key={period.id} value={period.id}>{period.code || period.description || period.id}</option>
              ))}
            </select>
          </label>
          <label>
            {t('maxPeriod')}
            <input type="number" min={1} max={60} value={trendMaxPeriods} onChange={(event) => setTrendMaxPeriods(Number(event.target.value) || 12)} />
          </label>
        </section>

        {trendMessage && <p className="dashboard-message">{trendMessage}</p>}

        <section className="rt-executive-grid">
          <article className="rt-exec-card rt-gauge-card">
            <header><span>{language.startsWith('en') ? 'Trend Score' : 'Trend Skoru'}</span><strong>{formatNumber(trendScore, 0)}</strong></header>
            <div className="rt-gauge" aria-hidden="true"><div className="rt-gauge-center">{formatNumber(trendScore, 0)}</div></div>
            <footer><span className={`rt-badge ${trendScore >= 75 ? 'positive' : trendScore >= 50 ? 'warn' : 'negative'}`}>{trendScore >= 75 ? t('strong') : trendScore >= 50 ? t('balanced') : t('risky')}</span></footer>
          </article>

          <article className="rt-exec-card">
            <header><span>{language.startsWith('en') ? 'Trend Direction' : 'Trend Yonu'}</span><strong>{trendDirection}</strong></header>
            <div className="rt-kpi-row"><span>{language.startsWith('en') ? 'Rising' : 'Yukselen'}</span><b>{trendSummary.up}</b></div>
            <div className="rt-kpi-row"><span>{language.startsWith('en') ? 'Falling' : 'Dusen'}</span><b>{trendSummary.down}</b></div>
            <div className="rt-kpi-row"><span>{t('flat')}</span><b>{trendSummary.flat}</b></div>
          </article>

          <article className="rt-exec-card">
            <header><span>{language.startsWith('en') ? 'Volatility' : 'Volatilite'}</span><strong>{formatNumber(volatility, 3)}</strong></header>
            <div className="rt-progress"><span style={{ width: `${clamp(volatility * 40, 0, 100)}%` }} /></div>
            <footer><span>{language.startsWith('en') ? 'Periodic volatility level' : 'Periodik oynaklik seviyesi'}</span></footer>
          </article>

          <article className="rt-exec-card">
            <header><span>{language.startsWith('en') ? 'Threshold Compliance' : 'Esik Uyum Orani'}</span><strong>{formatPercent(trendCoverage, 0)}</strong></header>
            <div className="rt-progress"><span style={{ width: `${trendCoverage}%` }} /></div>
            <footer><span>{`${violationsCount} ${language.startsWith('en') ? 'period violations' : 'donem ihlal'}`}</span></footer>
          </article>
        </section>
      </section>

      <section className="dashboard-section">
        <div className="section-header">
          <h2>{t('sectorRatioAnalysis')}</h2>
        </div>

        <div className="sr-top-head">
          <div>
            <h3>{language.startsWith('en') ? 'Sector Benchmark Intelligence' : 'Sektorel Benchmark Intelligence'}</h3>
            <p>{language.startsWith('en') ? 'Sector position, risk and deviation view for CEOs and CFOs.' : 'CEO ve CFO icin sektor konumu, risk ve sapma gorunumu.'}</p>
          </div>
          <button type="button" className="btn-primary sr-calc-btn" disabled={!hasSectorSelection || sectorLoading} onClick={handleSectorAnalyze}>
            {sectorLoading ? t('calculating') : t('analyze')}
          </button>
        </div>

        <CompanyPeriodSelector onSelectionChange={setSectorSelection} locale={language} />

        <section className="sector-ratio-analysis-controls">
          <label htmlFor="dashboard-benchmark-period-select">
            {language.startsWith('en') ? 'Sector Ratio Period' : 'Sektor Rasyo Donemi'}
            <select
              id="dashboard-benchmark-period-select"
              value={sectorBenchmarkPeriodId}
              onChange={(event) => setSectorBenchmarkPeriodId(event.target.value)}
              disabled={sectorBenchmarkPeriods.length === 0}
            >
              <option value="">{t('selectPeriod')}</option>
              {sectorBenchmarkPeriods.map((period) => (
                <option key={period.id} value={period.id}>
                   {period.code || period.description || `${t('periodN')} ${period.id}`}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="dashboard-sector-select">
            {t('benchmarkSector')}
            <select
              id="dashboard-sector-select"
              value={selectedSectorId}
              onChange={(event) => setSelectedSectorId(event.target.value)}
              disabled={!sectorBenchmarkPeriodId || sectorOptions.length === 0}
            >
              <option value="">{t('selectSector')}</option>
              {sectorOptions.map((sector) => (
                <option key={sector.id} value={sector.id}>
                  {sector.code} - {sector.name}
                </option>
              ))}
            </select>
          </label>
        </section>

        {sectorMessage && <p className="dashboard-message">{sectorMessage}</p>}

        <section className="sr-executive-grid">
          <article className="sr-exec-card sr-gauge-card">
            <header><span>{language.startsWith('en') ? 'Benchmark Score' : 'Benchmark Skoru'}</span><strong>{ratioFormatValue(benchmarkScore, null)}</strong></header>
            <div className="sr-gauge" aria-hidden="true"><div className="sr-gauge-center">{ratioFormatValue(benchmarkScore, null)}</div></div>
            <footer><span className={`sr-verdict sr-verdict-${benchmarkVerdict.toLowerCase()}`}>{benchmarkVerdict}</span></footer>
          </article>

          <article className="sr-exec-card">
            <header><span>{language.startsWith('en') ? 'Above Sector Density' : 'Sektor Ustu Yogunluk'}</span><strong>{sectorSummary.aboveCount}/{preparedSectorRows.length || 0}</strong></header>
            <div className="sr-progress"><span style={{ width: `${preparedSectorRows.length ? (sectorSummary.aboveCount / preparedSectorRows.length) * 100 : 0}%` }} /></div>
            <footer><span>{formatPercent(preparedSectorRows.length ? (sectorSummary.aboveCount / preparedSectorRows.length) * 100 : 0, 0)}</span></footer>
          </article>

          <article className="sr-exec-card">
            <header><span>{t('leverageRisk')}</span><strong>{leverageRiskLevel}</strong></header>
            <div className="sr-thermometer"><span style={{ height: `${Number.isNaN(leverageValue) || leverageSector <= 0 ? 0 : clamp((leverageValue / (leverageSector * 2)) * 100, 0, 100)}%` }} /></div>
            <footer><span>{`${t('debtEquity')}: ${ratioFormatValue(leverageValue, null)} | ${t('sector')}: ${ratioFormatValue(leverageSector, null)}`}</span></footer>
          </article>

          <article className="sr-exec-card">
            <header><span>AI Snapshot</span><strong>{sectorAiSnapshot.verdict}</strong></header>
            <div className="sr-mini-snapshot">
              <div><small>{t('score')}</small><b>{ratioFormatValue(sectorAiSnapshot.score, null)}</b></div>
              <div><small>{t('verdict')}</small><b>{sectorAiSnapshot.verdict}</b></div>
              <div><small>{t('criticalDeviation')}</small><b>{sectorAiSnapshot.critical}</b></div>
            </div>
          </article>
        </section>
      </section>

      <section className="dashboard-section">
        <div className="section-header">
          <h2>{t('aiPanelTitle')}</h2>
        </div>

        <div className="ai5-top-head">
          <div>
            <h3>{t('aiPanelTitle')}</h3>
            <p>{language.startsWith('en') ? 'Presents overall health and critical risks with Executive Snapshot + Drivers + Actions hierarchy.' : 'Executive Snapshot + Drivers + Actions hiyerarsisi ile sirketin genel sagligini ve kritik risklerini sunar.'}</p>
          </div>
          <div className="ai-header-actions">
            <button type="button" className="btn-secondary" onClick={() => loadAiAnalysis(aiSelection.companyId, aiSelection.periodId)} disabled={!hasAiSelection || aiLoadingAnalysis}>
              {aiLoadingAnalysis ? t('analysisLoading') : t('refreshAnalysis')}
            </button>
            <button type="button" className="btn-primary" disabled={!hasAiSelection || aiLoadingGenerate} onClick={handleAiGenerate}>
              {aiLoadingGenerate ? t('creating') : t('createAiReport')}
            </button>
          </div>
        </div>

        <CompanyPeriodSelector onSelectionChange={setAiSelection} locale={language} />

        <section className="ai-financial-report-controls">
          <label htmlFor="dash-ai-trend-start-period-select">{t('trendStart')}
            <select
              id="dash-ai-trend-start-period-select"
              value={aiTrendStartPeriodId}
              onChange={(event) => setAiTrendStartPeriodId(event.target.value)}
            >
              {filteredAiTrendStartPeriods.map((period) => (
                <option key={period.id} value={period.id}>{getPeriodLabel(period, language)}</option>
              ))}
            </select>
          </label>
          <label htmlFor="dash-ai-trend-end-period-select">{t('trendEnd')}
            <select
              id="dash-ai-trend-end-period-select"
              value={aiTrendEndPeriodId}
              onChange={(event) => setAiTrendEndPeriodId(event.target.value)}
            >
              {filteredAiTrendEndPeriods.map((period) => (
                <option key={period.id} value={period.id}>{getPeriodLabel(period, language)}</option>
              ))}
            </select>
          </label>
          <label htmlFor="dash-ai-trend-max-periods-input">{t('trendMax')}
            <input
              id="dash-ai-trend-max-periods-input"
              type="number"
              min="1"
              max="60"
              value={aiTrendMaxPeriods}
              onChange={(event) => setAiTrendMaxPeriods(Number(event.target.value) || 4)}
            />
          </label>
          <label htmlFor="dash-ai-temperature-input">{t('temperature')}
            <input id="dash-ai-temperature-input" type="number" min="0" max="1" step="0.1" value={aiTemperature} onChange={(event) => setAiTemperature(Number(event.target.value) || 0)} />
          </label>
        </section>

        {aiMessage && <p className="dashboard-message">{aiMessage}</p>}

        <section className="decision-kpi-grid">
          {aiKpiCards.map((item) => (
            <article key={item.key} className={`decision-kpi-card status-${item.status}`}>
              <div className="kpi-head"><span>{item.label}</span><strong>{item.value}</strong></div>
              <div className="kpi-meta"><span>{item.delta}</span><span>{item.note}</span></div>
              <Sparkline values={item.spark} />
            </article>
          ))}
        </section>

        <section className="ai-snapshot-row">
          <article className="snapshot-card gauge-card">
            <h3>{language.startsWith('en') ? 'Overall Status Score' : 'Genel Durum Skoru'}</h3>
            <div className="score-gauge">
              <div className={`score-gauge-inner ${aiScoreBand}`}><strong>{Math.round(aiHealthScore)}</strong><span>/100</span></div>
            </div>
            <p className="snapshot-verdict">{language.startsWith('en') ? 'AI Verdict' : 'AI Verdict'}: <strong>{aiVerdict}</strong></p>
          </article>

          <article className="snapshot-card executive-summary-card">
            <h3>{language.startsWith('en') ? 'Executive Summary' : 'Yonetici Ozeti'}</h3>
            <div className="callout-row"><div className="callout-pill good">{t('strong')}</div><p>{aiSummaryCards[0]}</p></div>
            <div className="callout-row"><div className="callout-pill risk">{t('risk')}</div><p>{aiSummaryCards[1]}</p></div>
            <div className="callout-row"><div className="callout-pill action">{t('action')}</div><p>{aiSummaryCards[2]}</p></div>
          </article>

          <article className="snapshot-card risk-alarm-card">
            <h3>{language.startsWith('en') ? 'Risk Alarm: Financial Leverage' : 'Risk Alarmi: Finansal Kaldirac'}</h3>
            <div className={`risk-level risk-${aiRiskLevel}`}><span>{aiRiskLevel === 'high' ? 'High' : aiRiskLevel === 'medium' ? 'Med' : aiRiskLevel === 'low' ? 'Low' : 'Unknown'}</span></div>
            <p>{t('debtToEquity')}: <strong>{formatNumber(aiDebtCurrent, 4)}</strong> {t('vsStandard')} <strong>{formatNumber(aiDebtStandard, 4)}</strong></p>
            <label className="simulator" htmlFor="dash-ai-debt-simulator">{t('whatIf')}
              <input
                id="dash-ai-debt-simulator"
                type="range"
                min={aiDebtStandard * 0.6}
                max={Math.max(aiDebtCurrent || aiDebtStandard, aiDebtStandard) * 1.2}
                step="0.01"
                value={aiDebtScenario || aiDebtStandard}
                onChange={(event) => setAiDebtScenario(Number(event.target.value))}
                disabled={!aiDebtValid}
              />
            </label>
            <div className="simulator-output">
               <span>{t('simulatedValue')}: {formatNumber(aiDebtScenario, 2)}</span>
               <span>{t('scoreImpact')}: +{formatNumber(aiSimGain, 1)}</span>
               <span>{t('newRisk')}: {aiSimLevel.toUpperCase()}</span>
            </div>
          </article>
        </section>
      </section>
    </div>
  );
}

export default DashboardPage;
